/**
 * Document Serializer
 *
 * Converts TipTap document to a simplified JSON format for LLM context.
 * The output is optimized for AI agents to understand document structure.
 */

import { Editor } from "@tiptap/react";
import { Node as ProseMirrorNode } from "@tiptap/pm/model";
import {
  SerializedNode,
  NodeAttrs,
  SerializedComment,
  SerializedTrackChange,
} from "./types";

/**
 * Serialize the document for LLM context
 *
 * Walks through the document and extracts all block nodes with their
 * IDs, types, content, and attributes.
 *
 * @param editor - The TipTap editor instance
 * @returns Array of serialized nodes
 *
 * @example
 * const nodes = serializeForLLM(editor);
 * // [
 * //   { id: "node-abc123", type: "heading", content: "Introduction", attrs: { level: 1 } },
 * //   { id: "node-def456", type: "paragraph", content: "This document describes..." },
 * // ]
 */
export function serializeForLLM(editor: Editor): SerializedNode[] {
  const nodes: SerializedNode[] = [];
  const doc = editor.state.doc;

  doc.descendants((node, pos) => {
    // Only include block nodes with IDs
    if (!node.isBlock || !node.attrs.id) {
      return true;
    }

    // Skip container nodes (we want their children)
    if (isContainerNode(node)) {
      return true;
    }

    const serialized = serializeNode(node);
    if (serialized) {
      nodes.push(serialized);
    }

    return true;
  });

  return nodes;
}

/**
 * Check if a node is a container (should be skipped in serialization)
 */
function isContainerNode(node: ProseMirrorNode): boolean {
  const containerTypes = [
    "doc",
    "bulletList",
    "orderedList",
    "table",
    "tableRow",
    // Footnote wrappers — their inner paragraphs serialize as normal blocks
    "footnotesSection",
    "footnoteItem",
  ];
  return containerTypes.includes(node.type.name);
}

/**
 * Serialize a single node
 */
function serializeNode(node: ProseMirrorNode): SerializedNode | null {
  const id = node.attrs.id;
  if (!id) return null;

  const type = node.type.name;
  const content = getTextContent(node);

  // Extract relevant attributes (exclude 'id' from attrs)
  const attrs = extractAttrs(node);

  // Extract marks (comments and track changes) from inline content
  const comments = extractComments(node);
  const trackChanges = extractTrackChanges(node);

  return {
    id,
    type,
    content,
    ...(Object.keys(attrs).length > 0 ? { attrs } : {}),
    ...(comments.length > 0 ? { comments } : {}),
    ...(trackChanges.length > 0 ? { trackChanges } : {}),
  };
}

/**
 * Get plain text content from a node
 */
function getTextContent(node: ProseMirrorNode): string {
  return node.textContent || "";
}

/**
 * Extract relevant attributes from a node
 * Excludes 'id' since it's already a top-level field
 */
function extractAttrs(node: ProseMirrorNode): NodeAttrs {
  const attrs: NodeAttrs = {};

  // Copy relevant attributes based on node type
  if (node.type.name === "heading" && node.attrs.level) {
    attrs.level = node.attrs.level;
  }

  if (node.attrs.textAlign && node.attrs.textAlign !== "left") {
    attrs.textAlign = node.attrs.textAlign;
  }

  // Parse and include patent specification metadata if present
  if (node.attrs.patentSequence) {
    try {
      const patentData =
        typeof node.attrs.patentSequence === "string"
          ? JSON.parse(node.attrs.patentSequence)
          : node.attrs.patentSequence;

      if (patentData.section) {
        attrs.section = patentData.section;
      }
      if (patentData.subSection) {
        attrs.subSection = patentData.subSection;
      }
      if (patentData.sequence !== undefined && patentData.sequence !== 0) {
        attrs.sequence = patentData.sequence;
      }
    } catch {
      // If parsing fails, include raw value
      attrs.patentSequence = node.attrs.patentSequence;
    }
  }

  // Parse and include claim metadata if present
  if (node.attrs.claimMetadata) {
    try {
      const claimData =
        typeof node.attrs.claimMetadata === "string"
          ? JSON.parse(node.attrs.claimMetadata)
          : node.attrs.claimMetadata;

      if (claimData.claimNumber !== undefined) {
        attrs.claimNumber = claimData.claimNumber;
      }
      if (claimData.dependsOn !== undefined) {
        attrs.dependsOn = claimData.dependsOn;
      }
    } catch {
      // If parsing fails, include raw value
      attrs.claimMetadata = node.attrs.claimMetadata;
    }
  }

  // Add any other custom attributes (but exclude handled attributes and default values)
  for (const [key, value] of Object.entries(node.attrs)) {
    if (key === "id") continue;
    if (key === "patentSequence") continue; // Already handled above
    if (key === "claimMetadata") continue; // Already handled above
    if (value === null || value === undefined) continue;
    if (key === "level" || key === "textAlign") continue; // Already handled

    // Include custom attributes
    attrs[key] = value;
  }

  return attrs;
}

/**
 * Extract comment marks from a node's inline content
 * Returns array of comments with their IDs and the text they're attached to
 */
function extractComments(node: ProseMirrorNode): SerializedComment[] {
  const comments: SerializedComment[] = [];
  const seenCommentIds = new Set<string>();

  // Walk through inline content to find comment marks
  node.descendants(child => {
    if (child.isText && child.marks) {
      child.marks.forEach(mark => {
        if (mark.type.name === "comment" && mark.attrs.commentId) {
          const commentId = mark.attrs.commentId;
          // Avoid duplicates (same comment can span multiple text nodes)
          if (!seenCommentIds.has(commentId)) {
            seenCommentIds.add(commentId);
            comments.push({
              commentId,
              text: child.text || "",
            });
          } else {
            // Extend the text for an existing comment
            const existing = comments.find(c => c.commentId === commentId);
            if (existing) {
              existing.text += child.text || "";
            }
          }
        }
      });
    }
    return true;
  });

  return comments;
}

/**
 * Extract track change marks (insertions/deletions) from a node's inline content
 * Returns array of track changes with their IDs, types, text, and metadata
 */
function extractTrackChanges(node: ProseMirrorNode): SerializedTrackChange[] {
  const trackChanges: SerializedTrackChange[] = [];
  const seenChangeIds = new Set<string>();

  // Walk through inline content to find insertion/deletion marks
  node.descendants(child => {
    if (child.isText && child.marks) {
      child.marks.forEach(mark => {
        const isInsertion = mark.type.name === "insertion";
        const isDeletion = mark.type.name === "deletion";

        if ((isInsertion || isDeletion) && mark.attrs.changeId) {
          const changeId = mark.attrs.changeId;
          const changeType = isInsertion ? "insertion" : "deletion";

          // Avoid duplicates (same change can span multiple text nodes)
          if (!seenChangeIds.has(changeId)) {
            seenChangeIds.add(changeId);
            trackChanges.push({
              changeId,
              type: changeType,
              text: child.text || "",
              ...(mark.attrs.author ? { author: mark.attrs.author } : {}),
              ...(mark.attrs.date ? { date: mark.attrs.date } : {}),
            });
          } else {
            // Extend the text for an existing change
            const existing = trackChanges.find(c => c.changeId === changeId);
            if (existing) {
              existing.text += child.text || "";
            }
          }
        }
      });
    }
    return true;
  });

  return trackChanges;
}

/**
 * Serialize document to a formatted string (for display/debugging)
 */
export function serializeToString(editor: Editor): string {
  const nodes = serializeForLLM(editor);
  return JSON.stringify(nodes, null, 2);
}

/**
 * Get a summary of the document structure
 * Useful for providing high-level context to LLM
 */
export function getDocumentSummary(editor: Editor): {
  nodeCount: number;
  headings: string[];
  wordCount: number;
} {
  const nodes = serializeForLLM(editor);

  const headings = nodes.filter(n => n.type === "heading").map(n => n.content);

  const wordCount = nodes
    .map(n => n.content.split(/\s+/).filter(Boolean).length)
    .reduce((a, b) => a + b, 0);

  return {
    nodeCount: nodes.length,
    headings,
    wordCount,
  };
}

/**
 * Find nodes by type
 */
export function findNodesByType(
  editor: Editor,
  type: string
): SerializedNode[] {
  return serializeForLLM(editor).filter(n => n.type === type);
}

/**
 * Find a node by ID from serialized output
 */
export function findSerializedNodeById(
  nodes: SerializedNode[],
  nodeId: string
): SerializedNode | undefined {
  return nodes.find(n => n.id === nodeId);
}
